import { Modal } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { CampaignBean } from 'src/api/api'
import { GoogleMaps, GoogleMapsClickEvent, useMap } from 'src/context/MapContext'

export interface MapPoint {
  lat?: number
  lon?: number
  radius?: number
}

interface Props {
  point: MapPoint
  setPoint: (point: MapPoint) => void
  mapVisible: boolean
  setMapVisible: (mapVisible: boolean) => void
}

export const fromBean = (item: CampaignBean): MapPoint => {
  return {
    lat: item.lat,
    lon: item.lon,
    radius: item.radius
  }
}

export const MapSelector: React.FC<Props> = ({ point, setPoint, mapVisible, setMapVisible }) => {

  const { Map, Circle, geocoder } = useMap()

  const [error, setError] = useState(undefined)
  const [searchString, setSearchString] = useState('')
  const [isCircleAdded, setIsCircleAdded] = useState(false)

  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<InstanceType<GoogleMaps['Map']>>()
  const circleRef = useRef<InstanceType<GoogleMaps['Circle']>>()

  const calcRadius = (pixelRadius, zoom, latitude) => {

    if (typeof latitude == 'function') {
      latitude = latitude()
    }

    const earthDiameter = 40075017
    const meters = earthDiameter * Math.cos(latitude * Math.PI / 180) / Math.pow(2, zoom + 8)
    return Math.round(pixelRadius * meters)
  }

  const removeCircle = () => {
    if (circleRef.current) {
      circleRef.current.setMap(null)
      circleRef.current = null
    }
  }

  const setCircle = (position: GoogleMaps['LatLng'] | { lat: number; lng: number, radius: number }) => {

    if (circleRef.current) {
      circleRef.current.setMap(null)
    }

    circleRef.current = new Circle({
      fillOpacity: 0.1,
      strokeWeight: 1,
      editable: true,
      map: mapRef.current,
      center: position,
      radius: position.radius || calcRadius(50, mapRef.current.zoom, position.lat)
    })

    circleRef.current.addListener('dblclick', (e: GoogleMapsClickEvent) => removeCircle())
    setIsCircleAdded(true)
  }

  useEffect(() => {
    if (mapRef.current) return
    if (!mapVisible) return

    mapRef.current = new Map(mapContainerRef.current as HTMLElement, {
      mapId: 'selection map',
      zoom: point.lat && point.lon ? 10 : 7,
      center: { lat: point.lat ?? 47.23, lng: point.lon ?? 19.53 },
      clickableIcons: false,
      disableDoubleClickZoom: true,
      streetViewControl: false,
      mapTypeControl: false
    })

    if (point.lat && point.lon && point.radius) {
      setCircle({ lat: point.lat, lng: point.lon, radius: point.radius })
    }

    mapRef.current.addListener('click', (e: GoogleMapsClickEvent) => setCircle(e.latLng as never))
  }, [mapVisible])

  const search = () => {
    geocoder
      .geocode({ address: searchString, language: 'en' })
      .then(({ results }) => {
        if (!results.length) {
          console.error('Empty Geocoder response')
          return
        }

        if (['APPROXIMATE', 'GEOMETRIC_CENTER'].includes(results[0].geometry.location_type.toString())) {
          console.log('Approximate location found. Please scroll map to select exact location')
        }

        mapRef.current.setZoom(16)
        mapRef.current.setCenter(results[0].geometry.location)
      })
      .catch(e => {
        setError(e)
      })
  }

  const submit = () => {

    if (!circleRef.current) {
      setPoint({})
      setMapVisible(false)
      setIsCircleAdded(false)
      return
    }

    geocoder
      .geocode({ location: circleRef.current.center, language: 'en' })
      .then(({ results }) => {
        if (!results.length) {
          console.error('Empty Geocoder response')
          return
        }

        const result = results.find((res) => res.types.includes('street_address')) ?? results[0]

        const data: MapPoint = {
          radius: circleRef.current.radius,
          lat: result.geometry.location.lat(),
          lon: result.geometry.location.lng(),
        }

        setPoint(data)
        setMapVisible(false)
      })
      .catch(e => {
        setError(e)
      })
  }

  return <Modal
    title="Area on map"
    centered
    open={mapVisible}
    onCancel={() => setMapVisible(false)}
    onOk={submit}
    okButtonProps={{ disabled: !isCircleAdded }}
    width={1024}
  >
    <div ref={mapContainerRef} style={{ height: 500 }} />
    <div>Use double-click to delete the selected area</div>
  </Modal>
}


// WEBPACK FOOTER //
// ./src/component/app/map/MapSelector.tsx